import { FEATURE_FLAG_DOMAIN } from '@/features/flags/constants';
import { OpenFeature, useBooleanFlagValue } from '@openfeature/react-sdk';

/**
 * Kill switch for the all-zero-from-start microphone escalation:
 * - the device-named `mic_never_energetic` warning variant at the monitor's
 *   20 s never-energetic alert (`CaptureSilenceRecovery`)
 * - the hard stop when NEITHER source has ever produced audio after the
 *   grace window (`SILENT_MIC_HARD_STOP_DELAY_MS`)
 * - the stop-time "no usable audio" gate in `useMutateStopRecording`, which
 *   blocks note generation and opens the mic-named reselection prompt
 *
 * Nested under `release-apps-silent-capture-warning-desktop` for the two UI
 * surfaces: the warning variant and the hard-stop timer both live inside
 * `CaptureSilenceRecovery`, which the outer flag gates.
 *
 * The stop-time gate is DELIBERATELY the exception, and it is covered by this
 * inner flag ALONE. `AUDIO_CAPTURE_ACTIVITY` is registered unconditionally, so
 * the `captureNeverEnergetic` latches are populated whatever the outer flag
 * says, and `resolveSilentCapture` runs inside a mutation callback — not a
 * hook context — while the outer flag exposes only a hook reader
 * (`useIsAppsSilentCaptureWarningDesktopEnabled`). Reading it there would mean
 * minting new imperative flag surface for a case this switch already covers.
 * Consequence to know when rolling out: with outer OFF and inner ON, a
 * user-initiated stop of a never-energetic recording still blocks note
 * generation and opens the mic-reselection prompt, with no warning having been
 * shown first. Turning THIS flag off removes that behaviour entirely, which is
 * what a kill switch is for — so do not ship inner ON with outer OFF.
 *
 * Default OFF — a false positive aborts a recording and blocks note
 * generation, and rollback needs a full app release. Kill switch, not an
 * experiment — do NOT add `useManualExposure`.
 */
export const DESKTOP_SILENT_MIC_HARD_STOP_FLAG_KEY =
  'release-apps-silent-mic-hard-stop-desktop';

export const useIsDesktopSilentMicHardStopEnabled = (): boolean => {
  return useBooleanFlagValue(DESKTOP_SILENT_MIC_HARD_STOP_FLAG_KEY, false);
};

/**
 * Imperative read for mutation callbacks (same pattern as
 * {@link isDesktopRecordingLivenessEnabled}).
 */
export const isDesktopSilentMicHardStopEnabled = (): boolean => {
  return OpenFeature.getClient(FEATURE_FLAG_DOMAIN).getBooleanValue(
    DESKTOP_SILENT_MIC_HARD_STOP_FLAG_KEY,
    false
  );
};
